/**
 * Created: 2026-09-20
 * Initial commit: Add addIotDevice mutation dialog for app admin device provisioning
 *
 * Dialog to create a new IoT device pre-assigned to a site. The device
 * starts unenrolled — cert/WireGuard fields are filled in later by a
 * separate enrollment flow, not here.
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { useState } from 'react'
import {
  AddIotDeviceDocument,
  type IotDevicesQuery,
  SitesDocument,
} from '../gql/graphql'

gql`
  mutation AddIotDevice($siteId: UUID!) {
    addIotDevice(siteId: $siteId) {
      deviceId
      site {
        id
        name
      }
      certSerial
      certExpiry
      lastSeen
      createdAt
    }
  }
`

gql`
  query Sites {
    sites {
      id
      name
    }
  }
`

type SiteOption = { id: string; name: string }

interface AddIotDeviceDialogProps {
  open: boolean
  onClose: () => void
  onAdded: (device: IotDevicesQuery['iotDevices'][number]) => void
}

export default function AddIotDeviceDialog({
  open,
  onClose,
  onAdded,
}: AddIotDeviceDialogProps) {
  const [site, setSite] = useState<SiteOption | null>(null)
  const { data: sitesData, loading: sitesLoading } = useQuery(SitesDocument, {
    skip: !open,
  })
  const sites = [...(sitesData?.sites ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  )

  const [addIotDevice, { loading }] = useMutation(AddIotDeviceDocument, {
    onCompleted: (data) => {
      onAdded(data.addIotDevice)
      handleClose()
    },
  })

  const handleClose = () => {
    setSite(null)
    onClose()
  }

  const handleSubmit = () => {
    if (site) {
      addIotDevice({ variables: { siteId: site.id } })
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>添加设备</DialogTitle>
      <DialogContent>
        <Autocomplete
          autoFocus
          options={sites}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={sitesLoading}
          value={site}
          onChange={(_, value) => setSite(value)}
          renderInput={(params) => (
            <TextField {...params} label="场馆" variant="outlined" required />
          )}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          取消
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !site}>
          创建
        </Button>
      </DialogActions>
    </Dialog>
  )
}
